import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import api from '../../utils/api';

const ActivityLog = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');

  // Helper function to format time to match "26 October 2025 at 01:53:16 UTC+1" format
  const formatTime = (timestamp) => {
    // Handle different timestamp formats
    let date;
    
    // If it's already a formatted string in our target format, return as is
    if (typeof timestamp === 'string' && 
        /^\d{1,2}\s+[A-Za-z]+\s+\d{4}\s+at\s+\d{2}:\d{2}:\d{2}\s+UTC[+-]\d+/.test(timestamp)) {
      return timestamp;
    }
    
    try {
      // Handle Firestore Timestamp object with _seconds and _nanoseconds
      if (timestamp && typeof timestamp === 'object' && '_seconds' in timestamp) {
        date = new Date(timestamp._seconds * 1000);
      }
      // Handle regular timestamp object with seconds and nanoseconds
      else if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle numeric timestamp (milliseconds)
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // Handle ISO string or other string formats
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      // Handle Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      else {
        return "Invalid date";
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      
      // Format the date
      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'long' });
      const year = date.getFullYear();
      
      // Format time with leading zeros
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      // Get timezone offset in hours and minutes
      const tzOffset = date.getTimezoneOffset();
      const tzSign = tzOffset <= 0 ? '+' : '-';
      const tzHours = String(Math.abs(Math.floor(tzOffset / 60))).padStart(2, '0');
      
      return `${day} ${month} ${year} at ${hours}:${minutes}:${seconds} UTC${tzSign}${tzHours}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  // Fetch activities from the API
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const response = await api.get('/activity', {
          params: {
            page,
            limit: 20,
            type: filter !== 'all' ? filter : undefined
          }
        });
        
        setActivities(response.data?.activities || []);
        setTotalPages(Math.ceil((response.data?.total || 0) / 20) || 1);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch activities:', err);
        setError('Failed to load activities');
        setActivities([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [page, filter, sortBy, sortOrder]);

  // Filter activities based on selected filter
  const filteredActivities = filter === 'all' 
    ? activities 
    : activities.filter(activity => activity.type === filter);

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Activity Log</h1>
          <p className="text-muted-foreground">Complete history of inventory activities and system events</p>
        </div>
        <Button variant="outline" iconName="ArrowLeft" onClick={() => navigate(-1)} className="text-xs sm:text-sm">
          Back to Dashboard
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-card mb-8">
        <div className="p-6 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:items-center sm:gap-4 sm:w-auto">
              <div className="relative w-full sm:w-auto sm:max-w-[180px]">
                <select
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setPage(1); // Reset to first page when filter changes
                  }}
                  className="appearance-none bg-background border border-border rounded-md py-2 pl-2 pr-7 text-xs sm:text-sm sm:pl-3 sm:pr-10 focus:outline-none focus:ring-2 focus:ring-primary/30 w-full"
                >
                  <option value="all">All Activities</option>
                  <option value="stock_adjustment">Stock Adjustments</option>
                  <option value="purchase_order">Purchase Orders</option>
                  <option value="low_stock_alert">Low Stock Alerts</option>
                  <option value="product_added">New Products</option>
                  <option value="product_updated">Product Updates</option>
                  <option value="product_deleted">Product Deletions</option>
                  <option value="inventory_audit">Inventory Audits</option>
                  <option value="stock_movement">Stock Movements</option>
                  <option value="supplier_update">Supplier Updates</option>
                  <option value="reorder_point">Reorder Points</option>
                  <option value="user_login">User Logins</option>
                  <option value="api_request">User Actions</option>
                  <option value="report_generated">Reports Generated</option>
                  <option value="product_sold">Products Checked Out</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                  <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
                </div>
              </div>

              <div className="relative w-full sm:w-auto sm:max-w-[180px]">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                    setPage(1); // Reset to first page when sort changes
                  }}
                  className="appearance-none bg-background border border-border rounded-md py-2 pl-2 pr-7 text-xs sm:text-sm sm:pl-3 sm:pr-10 focus:outline-none focus:ring-2 focus:ring-primary/30 w-full"
                >
                  <option value="timestamp-desc">Newest First</option>
                  <option value="timestamp-asc">Oldest First</option>
                  <option value="type-asc">Activity Type (A-Z)</option>
                  <option value="type-desc">Activity Type (Z-A)</option>
                  <option value="user-asc">User (A-Z)</option>
                  <option value="user-desc">User (Z-A)</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                  <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
                </div>
              </div>
            </div>

            <Button 
              variant="outline" 
              iconName="RefreshCw" 
              onClick={() => {
                setLoading(true);
                api.get('/activity', {
                  params: {
                    page,
                    limit: 20,
                    sort: sortBy,
                    order: sortOrder,
                    type: filter !== 'all' ? filter : undefined
                  }
                })
                  .then(response => {
                    setActivities(response.data?.activities || []);
                    setTotalPages(Math.ceil((response.data?.total || 0) / 20) || 1);
                    setError(null);
                  })
                  .catch(err => {
                    console.error('Failed to refresh activities:', err);
                    setError('Failed to refresh activities');
                  })
                  .finally(() => setLoading(false));
              }}
            >
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-[400px] text-center p-4">
            <Icon name="AlertTriangle" size={32} className="text-warning mb-4" />
            <p className="text-muted-foreground text-lg">{error}</p>
            <Button 
              variant="outline" 
              className="mt-6"
              onClick={() => {
                setLoading(true);
                api.get('/activity', {
                  params: {
                    page,
                    limit: 20,
                    sort: sortBy,
                    order: sortOrder,
                    type: filter !== 'all' ? filter : undefined
                  }
                })
                  .then(response => {
                    setActivities(response.data?.activities || []);
                    setTotalPages(Math.ceil((response.data?.total || 0) / 20) || 1);
                    setError(null);
                  })
                  .catch(err => {
                    console.error('Failed to refresh activities:', err);
                    setError('Failed to refresh activities');
                  })
                  .finally(() => setLoading(false));
              }}
            >
              Try Again
            </Button>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[400px] text-center p-4">
            <Icon name="Inbox" size={32} className="text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-lg">No activities found</p>
            {filter !== 'all' && (
              <Button 
                variant="outline" 
                className="mt-6"
                onClick={() => setFilter('all')}
              >
                Show All Activities
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <button 
                      className="flex items-center space-x-1"
                      onClick={() => handleSortChange('type')}
                    >
                      <span>Type</span>
                      {sortBy === 'type' && (
                        <Icon 
                          name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
                          size={12} 
                          className="text-primary"
                        />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <span>Details</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <button 
                      className="flex items-center space-x-1"
                      onClick={() => handleSortChange('user')}
                    >
                      <span>User</span>
                      {sortBy === 'user' && (
                        <Icon 
                          name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
                          size={12} 
                          className="text-primary"
                        />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <button 
                      className="flex items-center space-x-1"
                      onClick={() => handleSortChange('timestamp')}
                    >
                      <span>Date & Time</span>
                      {sortBy === 'timestamp' && (
                        <Icon 
                          name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
                          size={12} 
                          className="text-primary"
                        />
                      )}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredActivities.map((activity) => (
                  <tr key={activity.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${activity.color || 'bg-primary/10 text-primary'}`}>
                          <Icon name={activity.icon || 'Activity'} size={16} />
                        </div>
                        <span className="text-sm font-medium text-foreground">
                          {activity.title || activity.type?.replace(/_/g, ' ') || 'Activity'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-muted-foreground">{activity.description || 'No additional details'}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="User" size={12} className="text-primary" />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {activity.user || activity.userName || activity.userEmail || activity.userId || 'Unknown user'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatTime(activity.timestamp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && filteredActivities.length > 0 && (
          <div className="px-6 py-4 border-t border-border flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing page {page} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                iconName="ChevronLeft"
                disabled={page === 1}
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                iconName="ChevronRight"
                iconPosition="right"
                disabled={page === totalPages}
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;